/**
 * useMatching — calls POST /api/matching/rank after every user-turn and exposes
 * a top-3 ranking the MatchingPanel can render.
 *
 * Throttles during voice-dictation to one call per 4 user-utterances, matching
 * the existing pattern in WorkspacePage's voice→document sync.
 */
import { useEffect, useRef, useState } from 'react';
import { useAppToken } from './useAppToken';

const API_URL = process.env.REACT_APP_API_URL || '';

export function useMatching(messages, voiceStatus) {
  const { getToken } = useAppToken();

  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(false);
  const [latencyMs, setLatencyMs] = useState(null);
  const [error, setError] = useState(null);

  const lastUserTurnCountRef = useRef(0);
  const inFlightRef = useRef(false);

  useEffect(() => {
    const userTurns = (messages || []).filter(m => m && m.sender === 'user' && m.content).length;
    if (userTurns === 0) return;
    if (userTurns === lastUserTurnCountRef.current) return;

    const isVoice = voiceStatus === 'connected';
    if (isVoice && userTurns - lastUserTurnCountRef.current < 4) return;

    if (inFlightRef.current) return;
    inFlightRef.current = true;

    const payload = {
      messages: messages
        .filter(m => m && (m.sender === 'user' || m.sender === 'ai') && m.content)
        .slice(-20)
        .map(m => ({ sender: m.sender, text: m.content }))
    };

    setLoading(true);
    setError(null);

    (async () => {
      try {
        let token = null;
        try { token = await getToken(); } catch { /* no auth in demo */ }
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;

        const res = await fetch(`${API_URL}/api/matching/rank`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setRanking(Array.isArray(data.ranking) ? data.ranking : []);
        setLatencyMs(typeof data.modelLatencyMs === 'number' ? data.modelLatencyMs : null);
        lastUserTurnCountRef.current = userTurns;
      } catch (e) {
        console.error('[useMatching] error', e);
        setError(e.message || String(e));
      } finally {
        setLoading(false);
        inFlightRef.current = false;
      }
    })();
  }, [messages, voiceStatus, getToken]);

  return { ranking, loading, latencyMs, error };
}
